import { sha256 } from 'js-sha256';
import { isLocalhost, isDeployTestEnv } from './runtime';
import { card } from 'creditcards';
import { TFunction } from 'i18next';
import { EmergencyForm, IdentifierType, MopType, UserInfoForm } from '../types';
import { encryptAleMop, encryptCryptexMop } from './encrypt';

export * from "./runtime";
export * from "./schemas";
export * from "./encrypt";

export const getPortalUrl = () => {
  return isLocalhost() || isDeployTestEnv()
    ? import.meta.env.VITE_LE_PORTAL_PUBLIC_URL_TEST
    : import.meta.env.VITE_LE_PORTAL_PUBLIC_URL_PROD;
};

export function parseCreditCard(mopValue: string) {
  return {
    value: mopValue,
    formatted: card.format(mopValue),
    isValid: card.isValid(mopValue),
    type: card.type(mopValue),
  };
}

// Sha256 encrypted IBANs. These are IBANs that are not allowed to be used as valid IBANs when submitting a ticket.
const BLOCKED_IBANS = [
  'caf24eb99167d51d791e0564156a3a82779206a6b651ee6c3b5a20cd4846985b',
];

/**
 * Utility function to check if an IBAN being submitted has been explicitly blocked.
 * @param iban IBAN to check against the list of blocked IBANs.
 * @returns Returns true if this IBAN is blocked, false otherwise.
 */
export const isIbanBlocked = (iban: string | undefined): boolean => {
  if (!iban) return false;
  const hashedIban = sha256(iban);
  return BLOCKED_IBANS.includes(hashedIban);
};

export function enumToDropdownOptions(
  input: Record<string, unknown>,
  t: TFunction
) {
  const cardOptions = [];
  for (const [key, value] of Object.entries(input)) {
    const label = t ? t(value as string) : (value as string);
    cardOptions.push({ label: label, value: key as string });
  }
  return cardOptions;
}

export const getEmergencyFromFormValues = async (
  formValues: EmergencyForm,
  userInfo: UserInfoForm,
  requestId: string,
  publicKey: string,
  locale: string) => {

  const identifierPayloads = [];
  for (const identifier of formValues.memberIdPayload.identifierPayloads) {
    if (identifier.mopType.value === MopType.US_BA) {
      identifier.mop = `${identifier.routingNumber}::${identifier.accountNumber}`
    } else if (identifier.mopType.value === MopType.BR_BA) {
      identifier.mop = `${identifier.branchNumber}::${identifier.accountNumber}::${identifier.digit}`
    }
    const plaintextMop = identifier.mop;
    const cryptexMop = await encryptCryptexMop(plaintextMop, publicKey);
    const encryptedMop = await encryptAleMop(plaintextMop, identifier.mopType.value);
    identifierPayloads.push({
      ...identifier,
      mop: cryptexMop,
      encryptedMop,
      mopType: identifier.mopType.value,
      type: identifier.mopType.value === MopType.GIFT_CARD ? IdentifierType.GIFT_CARD : IdentifierType.MOP,
    });
  }

  return {
    eventDate: formValues.eventDate,
    description1: formValues.description1,
    description2: formValues.description2,
    description3: formValues.description3,
    description4: formValues.description4,
    memberIdPayload: {
      emailAddresses: formValues.memberIdPayload.emailAddresses,
      identifierPayloads,
      phoneNumbers: formValues.memberIdPayload.phoneNumbers,
      other: formValues.memberIdPayload.other,
    },
    signature: formValues.signature,
    consentDate: formValues.consentDate,
    userInfo: getUserInfoFromFormValues(userInfo!, locale),
    requestId,
  };
}

export const getUserInfoFromFormValues = (formValues: UserInfoForm, locale: string) => ({
  agencyName: formValues.agencyName,
  agencyAddress: {
    state: formValues.agencyAddress.state?.value || "",
    street1: formValues.agencyAddress.street1,
    street2: formValues.agencyAddress.street2 || "",
    city: formValues.agencyAddress.city,
    zipCode: formValues.agencyAddress.zipCode,
    country: formValues.agencyAddress.country.value,
    primary: true,
    type: 'business',
  },
  emailAddress: formValues.emailAddress,
  familyName: formValues.familyName,
  givenName: formValues.givenName,
  phoneNumber: formValues.phoneNumber,
  title: formValues.title,
  preferredLanguage: locale,
});

export const SUPPORTED_LANGUAGES: { [index: string]: string } = {
  da: 'Danish',
  de: 'Deutsch',
  en: 'English',
  'en-GB': 'English (United Kingdom)',
  es: 'Español',
  'es-ES': 'Español (España)',
  fr: 'Français',
  he: 'עברית',
  it: 'Italiano',
  hu: 'Magyar',
  nb: 'Norwegian Bokmål',
  nl: 'Nederlands',
  pl: 'Polskie',
  pt: 'Português (Brasil)',
  'pt-PT': 'Português (Portugal)',
  ro: 'Română',
  fi: 'Suomi',
  sv: 'svenska',
  cs: 'čeština',
  el: 'Ελληνικά',
};
